/**
 * Ready-made skills: tested skills for connected apps (backend agent_studio/packages,
 * GET/POST /{bot}/packages). The library modal in two steps (all apps with their logo,
 * template count and connection; then one app's templates, for Wix grouped by sub-app),
 * the suggestion card for a connected app whose skills are missing, and a plain yes/no
 * dialog (used for dearer AI levels). The modals use the platform's modal look (MUI Modal,
 * as addBehaviorModal.js). Settings of an installed skill are changed by Mo (the skill's
 * Edit button, Robert 17.10.2026), not in a dialog.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, InputBase, Modal, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare, faBookOpen, faChevronLeft, faChevronRight, faCircleCheck, faEnvelopeCircleCheck, faEye,
  faPlus, faPuzzlePiece, faRotate, faWandMagicSparkles, faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { providerMeta } from '../providerMeta';
import { MUI_CONTAINED as CONTAINED, MUI_OUTLINED as OUTLINED, Spinner } from './ui';

const API = process.env.REACT_APP_API_BASE_URL;


// the platform modal box (also the conversation list's search and rename dialogs)
export function useBoxSx(maxWidth) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  return {
    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'white',
    borderRadius: '10px', boxShadow: 24, p: isMobile ? 2.5 : 3.5, width: isMobile ? '80%' : '90%', maxWidth,
    maxHeight: '80vh', overflowY: 'auto', outline: 'none',
  };
}

/** packages grouped by app (and, within an app, by sub-app), in catalog order */
export function byProvider(packages) {
  const apps = [];
  for (const p of packages) {
    let a = apps.find((x) => x.provider === p.provider);
    if (!a) apps.push(a = { provider: p.provider, label: p.provider_label, connected: p.connected, items: [], groups: [] });
    a.items.push(p);
    let g = a.groups.find((x) => x.key === (p.group || ''));
    if (!g) a.groups.push(g = { key: p.group || '', label: p.group_label || '', items: [] });
    g.items.push(p);
  }
  return apps;
}

export const connectUrl = (provider) => `/agent-studio/apps?app=${encodeURIComponent(provider)}`;
// the Wix sub-app logos of the Apps page (public/wix)
export const groupLogo = (provider, group) => (provider === 'wix' && group ? `/wix/${group}.png` : null);

export function AppLogo({ provider, src, size = 'md' }) {
  const logo = src || providerMeta(provider)?.logo;
  return (
    <span className={`as-pkg-logo is-${size}`} aria-hidden="true">
      {logo ? <img src={logo} alt="" /> : <FontAwesomeIcon icon={faPuzzlePiece} />}
    </span>
  );
}

function ConnectionStatus({ app }) {
  const { t } = useTranslation();
  return app.connected
    ? <span className="as-pkg-conn is-on"><span className="as-pkg-dot" /> {t('agent_studio_pkg_connected')}</span>
    : <span className="as-pkg-conn"><span className="as-pkg-dot" /> {t('agent_studio_pkg_not_connected')}</span>;
}

const countText = (t, n) => t(n === 1 ? 'agent_studio_pkg_count_one' : 'agent_studio_pkg_count', { n });

/**
 * The library: first all apps (logo, number of templates, connection), then one app's window (AppView). From the
 * Apps tab the window opens directly (`single`: no way back to all apps).
 */
export function PackageLibraryModal({ open, onClose, packages, onInstall, botId, initialProvider = null, single = false }) {
  const { t } = useTranslation();
  const sx = useBoxSx(640);
  const [busy, setBusy] = useState('');
  const [provider, setProvider] = useState(initialProvider);
  useEffect(() => { if (open) setProvider(initialProvider); }, [open, initialProvider]);
  const apps = packages ? byProvider(packages) : [];
  const app = provider && apps.find((a) => a.provider === provider);
  const add = async (key) => {
    setBusy(key);
    await onInstall(key);
    setBusy('');
  };
  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={sx} className="as-modal" role="dialog" aria-label={t('agent_studio_pkg_library_title')}>
        {app && !single && (
          <button type="button" className="as-pkg-back" onClick={() => setProvider(null)}>
            <FontAwesomeIcon icon={faChevronLeft} /> {t('agent_studio_pkg_all_apps')}
          </button>
        )}
        <div className="as-modal-head">
          {app ? (
            <div className="as-pkg-app-title">
              <AppLogo provider={app.provider} size="lg" />
              <h3 className={WORDMARK_LOGOS.has(app.provider) ? 'as-sr-only' : ''}>{app.label}</h3>
              {app.connected ? <ConnectionStatus app={app} /> : (
                <a className="as-pkg-connect" href={connectUrl(app.provider)} target="_blank" rel="noopener noreferrer">
                  {t('agent_studio_pkg_connect', { app: app.label })} <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                </a>
              )}
            </div>
          ) : <h3>{t('agent_studio_pkg_library_title')}</h3>}
          <button type="button" className="as-modal-close" onClick={onClose} aria-label={t('close')}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        {packages === null && <div className="as-pkg-loading"><Spinner size={16} /></div>}

        {packages && !app && (
          <>
            <p className="as-modal-sub">{t('agent_studio_pkg_library_sub')}</p>
            <div className="as-pkg-apps">
              {apps.map((a) => {
                const active = a.items.filter((p) => p.installed).length;
                return (
                  <button key={a.provider} type="button" className="as-pkg-app-tile" onClick={() => setProvider(a.provider)}>
                    <AppLogo provider={a.provider} />
                    <span className="as-pkg-app-main">
                      <span className="as-pkg-app-name">{a.label}</span>
                      <span className="as-pkg-app-count">{countText(t, a.items.length)}</span>
                      <span className="as-pkg-app-status">
                        <ConnectionStatus app={a} />
                        {active > 0 && (
                          <span className="as-pkg-app-active">
                            {t(active === 1 ? 'agent_studio_pkg_active_n_one' : 'agent_studio_pkg_active_n', { n: active })}
                          </span>
                        )}
                      </span>
                    </span>
                    <FontAwesomeIcon icon={faChevronRight} className="as-pkg-app-chev" />
                  </button>
                );
              })}
            </div>
          </>
        )}

        {app && (
          <AppView app={app} botId={botId} busy={busy} onAdd={add} onClose={onClose} />
        )}
      </Box>
    </Modal>
  );
}

// the Wix sub-app whose live data "Show data" previews (backend dimensions); Wix Stores' products are knowledge
const WIX_GROUP_DATA = { bookings: 'services', events: 'events', plans: 'plans', restaurants: 'menu', blog: 'blog', crm: 'contacts' };
// logos that already spell the app's name: the window's head shows no name next to them
const WORDMARK_LOGOS = new Set(['wix']);
const DATA_FIRST = 10;
const DATA_STEP = 20;

/**
 * One app (Robert, 17.10.2026): its skills grouped by sub-app (Wix Bookings, Wix Stores …), each group folds open
 * to its skills and its live data; Wix Stores' products lead to the Wix knowledge page.
 */
function AppView({ app, botId, busy, onAdd, onClose }) {
  const { t } = useTranslation();
  const single = app.groups.length === 1;
  return (
    <div className="as-appwin">
      {!app.connected && <p className="as-modal-sub">{t('agent_studio_pkg_app_sub_connect', { app: app.label })}</p>}
      {app.groups.map((g) => (
        <GroupSection key={g.key || 'all'} app={app} group={g} botId={botId} busy={busy} onAdd={onAdd} onClose={onClose}
          startOpen={single || !g.key} />
      ))}
      {app.provider === 'wix' && app.connected && (
        <div className="as-appwin-foot">
          <KnowledgeLink onGo={onClose} label={t('agent_studio_app_manage_knowledge')} />
        </div>
      )}
    </div>
  );
}

function GroupSection({ app, group: g, botId, busy, onAdd, onClose, startOpen }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(startOpen);
  const [data, setData] = useState(false);
  const bodyId = useId();
  const dim = app.provider === 'wix' && app.connected ? WIX_GROUP_DATA[g.key] : null;
  const products = app.provider === 'wix' && app.connected && g.key === 'stores';
  const active = g.items.filter((p) => p.installed).length;
  const cards = g.items.map((p) => (
    <div key={p.key} className={`as-pkg-card ${p.connected ? '' : 'is-off'}`} role="group" aria-label={p.name}>
      <div className="as-pkg-main">
        <div className="as-pkg-name">{p.name}</div>
        <div className="as-pkg-summary">{p.summary}</div>
        {p.needs_verification && (
          <div className="as-pkg-note"><FontAwesomeIcon icon={faEnvelopeCircleCheck} /> {t('agent_studio_pkg_verifies')}</div>
        )}
      </div>
      <div className="as-pkg-side">
        <PackageState p={p} busy={busy === p.key} disabled={!!busy} onAdd={() => onAdd(p.key)} />
      </div>
    </div>
  ));
  if (!g.key) return <section className="as-pkg-group is-plain">{cards}</section>;
  return (
    <section className={`as-pkg-group ${open ? 'is-open' : ''}`}>
      <button type="button" className="as-pkg-group-head" aria-expanded={open} aria-controls={bodyId}
        onClick={() => setOpen((o) => !o)}>
        <FontAwesomeIcon icon={faChevronRight} className={`as-chevron ${open ? 'is-open' : ''}`} />
        <AppLogo provider={app.provider} src={groupLogo(app.provider, g.key)} size="sm" />
        <span className="as-pkg-group-name">{g.label}</span>
        <span className="as-pkg-group-meta">
          {countText(t, g.items.length)}
          {active > 0 && ` · ${t(active === 1 ? 'agent_studio_pkg_active_n_one' : 'agent_studio_pkg_active_n', { n: active })}`}
        </span>
      </button>
      <div id={bodyId} className={`as-fold ${open ? 'is-open' : ''}`} aria-hidden={!open} {...(open ? {} : { inert: '' })}>
        <div className="as-fold-inner">
          <div className="as-pkg-group-body">
            {(dim || products) && (
              <div className="as-appdata-bar">
                {dim && (
                  <button type="button" className="as-btn-link as-appdata-toggle" aria-expanded={data}
                    onClick={() => setData((v) => !v)}>
                    <FontAwesomeIcon icon={faEye} /> {t(data ? 'agent_studio_app_hide_data' : 'agent_studio_app_show_data')}
                  </button>
                )}
                {products && <KnowledgeLink onGo={onClose} label={t('agent_studio_app_products_knowledge')} />}
              </div>
            )}
            {dim && data && <GroupData provider={app.provider} label={g.label} botId={botId} dim={dim} />}
            {cards}
          </div>
        </div>
      </div>
    </section>
  );
}

/** Wix content that is the agent's knowledge (products, site content): the link leads to the Wix knowledge page. */
function KnowledgeLink({ onGo, label }) {
  const navigate = useNavigate();
  return (
    <button type="button" className="as-btn-link as-appdata-toggle"
      onClick={() => { if (onGo) onGo(); navigate('/knowledge/wix'); }}>
      <FontAwesomeIcon icon={faBookOpen} /> {label}
    </button>
  );
}

const shownValue = (v) => (v == null || v === '' ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v));

/**
 * A sub-app's live data (what the agent itself reads), fetched on demand, nothing stored. The server returns a
 * capped first page (e.g. 50 contacts); it is shown 10 at a time, then 20 more per click, with a note at the cap.
 */
function GroupData({ provider, label, botId, dim }) {
  const { t } = useTranslation();
  const [state, setState] = useState({ loading: true });
  const [shown, setShown] = useState(DATA_FIRST);
  useEffect(() => {
    let off = false;
    (async () => {
      try {
        const r = await fetch(`${API}apps/${encodeURIComponent(provider)}/dimensions/${encodeURIComponent(dim)}/preview?bot_id=${encodeURIComponent(botId)}`);
        const data = await r.json().catch(() => ({}));
        if (!off) setState(r.ok && !data.error ? { data } : { error: true });
      } catch (_) {
        if (!off) setState({ error: true });
      }
    })();
    return () => { off = true; };
  }, [provider, botId, dim]);

  if (state.loading) return <div className="as-appdata is-note"><Spinner size={12} /> {t('agent_studio_app_data_loading', { app: label })}</div>;
  if (state.error) return <div className="as-appdata is-note">{t('agent_studio_app_data_error')}</div>;
  const { data } = state;
  if (data.unavailable) return <div className="as-appdata is-note">{t('agent_studio_app_data_unavailable')}</div>;
  const items = data.items || [];
  if (!items.length) return <div className="as-appdata is-note">{t('agent_studio_app_data_none')}</div>;
  // the first readable field is the line's title, the next ones its details; ids and links stay out
  const fields = (data.fields?.length ? data.fields : Object.keys(items[0])).filter((f) => !/(^id|_id|_url|url)$/.test(f));
  const [main, ...rest] = fields;
  const visible = items.slice(0, shown);
  const left = items.length - visible.length;
  return (
    <div className="as-appdata">
      <ul>
        {visible.map((it, i) => (
          <li key={i}>
            <span className="as-appdata-main">{shownValue(it[main])}</span>
            <span className="as-appdata-sub">{rest.slice(0, 3).map((f) => shownValue(it[f])).filter(Boolean).join(' · ')}</span>
          </li>
        ))}
      </ul>
      {left > 0 ? (
        <button type="button" className="as-btn-link as-appdata-more" onClick={() => setShown((n) => n + DATA_STEP)}>
          {t('agent_studio_app_data_show_more', { n: Math.min(DATA_STEP, left) })}
        </button>
      ) : items.length >= DATA_STEP && (
        <div className="as-appdata-cap">{t('agent_studio_app_data_cap', { n: items.length, app: label })}</div>
      )}
    </div>
  );
}

function PackageState({ p, busy, disabled, onAdd }) {
  const { t } = useTranslation();
  if (p.installed && p.update_available) {
    return (
      <button type="button" className="as-btn-portal" disabled={disabled} onClick={onAdd}>
        {busy ? <Spinner size={11} /> : <FontAwesomeIcon icon={faRotate} />} {t('agent_studio_pkg_update')}
      </button>
    );
  }
  if (p.installed) return <span className="as-pkg-state is-on"><FontAwesomeIcon icon={faCircleCheck} /> {t('agent_studio_pkg_active')}</span>;
  if (p.detached) return <span className="as-pkg-state" title={t('agent_studio_pkg_detached_hint')}>{t('agent_studio_pkg_detached')}</span>;
  if (!p.connected) return null;
  return (
    <button type="button" className="as-btn-portal" disabled={disabled} onClick={onAdd}>
      {busy ? <Spinner size={11} /> : <FontAwesomeIcon icon={faPlus} />} {t('add')}
    </button>
  );
}

/** "Wix is connected: ready-made skills for it" at the top of the skills tab. */
export function PackageSuggestion({ group, onShow, onAdd, onDismiss }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const one = group.items.length === 1 ? group.items[0] : null;
  const add = async () => {
    setBusy(true);
    await onAdd(one.key);
    setBusy(false);
  };
  return (
    <div className="as-pkg-suggest">
      <span className="as-pkg-suggest-ic"><FontAwesomeIcon icon={faPuzzlePiece} /></span>
      <div className="as-pkg-suggest-main">
        <div className="as-pkg-suggest-title">{t('agent_studio_pkg_suggest_title', { app: group.label })}</div>
        <div className="as-pkg-suggest-text">
          {one ? t('agent_studio_pkg_suggest_one', { name: one.name })
            : t('agent_studio_pkg_suggest_many', { n: group.items.length })}
        </div>
      </div>
      <div className="as-pkg-suggest-actions">
        {one ? (
          <button type="button" className="as-btn-portal" disabled={busy} onClick={add}>
            {busy ? <Spinner size={11} /> : <FontAwesomeIcon icon={faPlus} />} {t('add')}
          </button>
        ) : (
          <button type="button" className="as-btn-portal" onClick={onShow}>{t('agent_studio_pkg_show')}</button>
        )}
        <button type="button" className="as-trash-btn" title={t('agent_studio_pkg_dismiss')} aria-label={t('agent_studio_pkg_dismiss')}
          onClick={onDismiss}>
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>
    </div>
  );
}

/**
 * A skill's Edit (Robert, 17.10.2026): only the fixed sentence ("I'd like to change the skill …") at the top and,
 * below it, optionally what should change. "Start change" always hands it to Mo, who starts at once and asks if
 * nothing was said; only while Mo is busy does the start wait.
 */
/** A request to Mo: the fixed first sentence on top, the owner's wish below (optional), then Mo starts with both. */
function MoRequestDialog({ intro, withWish, placeholder, startLabel, busy, onClose, onStart }) {
  const { t } = useTranslation();
  const sx = useBoxSx(560);
  const [wish, setWish] = useState('');
  const [sending, setSending] = useState(false);
  const field = useRef(null);
  const headId = useId();
  useEffect(() => { field.current?.focus(); }, []);
  const ready = !busy && !sending;
  const start = async () => {
    if (!ready) return;
    setSending(true);
    const w = wish.trim();
    await onStart(w ? withWish(w) : intro);
    setSending(false);
    onClose();
  };
  return (
    <Modal open onClose={onClose}>
      <Box sx={sx} className="as-modal" role="dialog" aria-labelledby={headId}>
        <div className="as-edit-fixed" id={headId}>
          <FontAwesomeIcon icon={faWandMagicSparkles} />
          <span>{intro}</span>
        </div>
        <div className="as-edit-box" onClick={() => field.current?.focus()}>
          <InputBase inputRef={field} multiline minRows={3} maxRows={8} fullWidth className="as-edit-input"
            value={wish} onChange={(e) => setWish(e.target.value)} placeholder={placeholder}
            inputProps={{ 'aria-label': placeholder, maxLength: 4000 }}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) start(); }} />
        </div>
        {busy && <p className="as-edit-hint is-busy">{t('agent_studio_skill_edit_busy')}</p>}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 2 }}>
          <Button onClick={onClose} variant="outlined" sx={OUTLINED}>{t('cancel')}</Button>
          <Button onClick={start} variant="contained" sx={CONTAINED} disabled={!ready}>
            {sending ? <Spinner size={14} /> : startLabel}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

/** A skill's Edit: every change goes through Mo (Robert, 17.10.2026). */
export function EditSkillDialog({ name, busy, onClose, onStart }) {
  const { t } = useTranslation();
  return (
    <MoRequestDialog intro={t('agent_studio_skill_edit_intro', { name })}
      withWish={(wish) => t('agent_studio_skill_edit_with', { name, wish })}
      placeholder={t('agent_studio_skill_edit_placeholder')} startLabel={t('agent_studio_skill_edit_start')}
      busy={busy} onClose={onClose} onStart={onStart} />
  );
}

/** "Add skill": the same dialog, Mo builds the new skill (Robert, 18.10.2026). */
export function AddSkillDialog({ busy, onClose, onStart }) {
  const { t } = useTranslation();
  return (
    <MoRequestDialog intro={t('agent_studio_skill_add_intro')}
      withWish={(wish) => t('agent_studio_skill_add_with', { wish })}
      placeholder={t('agent_studio_skill_add_placeholder')} startLabel={t('agent_studio_skill_add_start')}
      busy={busy} onClose={onClose} onStart={onStart} />
  );
}

/** A yes/no question in the platform's modal look (title, text, Cancel + one action). */
export function ConfirmDialog({ open, title, text, action, onClose, onConfirm }) {
  const { t } = useTranslation();
  const sx = useBoxSx(420);
  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={sx} className="as-modal">
        <h3>{title}</h3>
        <p className="as-modal-text">{text}</p>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3 }}>
          <Button onClick={onClose} variant="outlined" sx={OUTLINED}>{t('cancel')}</Button>
          <Button onClick={() => { onConfirm(); onClose(); }} variant="contained" sx={CONTAINED}>{action}</Button>
        </Box>
      </Box>
    </Modal>
  );
}
